import { formatDate, type DatePreset } from './format-date';

/**
 * A calendar day served as `YYYY-MM-DD` (storage format) in a preset, read
 * at UTC midnight so it never slips a day in the reader's zone. Anything
 * else comes back as given.
 */
export function formatDay(
  day: string,
  preset: DatePreset = 'day-month'
): string {
  return formatDate(`${day}T00:00:00.000Z`, preset) ?? day;
}

/**
 * A calendar month served as `YYYY-MM` (storage format) in a preset,
 * default "May 2026", read at UTC so it never slips a month. Anything else
 * comes back as given.
 */
export function formatMonth(
  month: string,
  preset: DatePreset = 'month-year'
): string {
  return formatDate(`${month}-01T00:00:00.000Z`, preset) ?? month;
}

/**
 * A served day that arrives either as a storage day (`YYYY-MM-DD`, read at
 * UTC midnight) or as a full ISO moment, in a preset: both
 * `2021-03-01` and `2021-03-01T00:00:00.000Z` read "Mar 1, 2021" with
 * `day-month-year`. Anything unreadable comes back as given.
 */
export function formatServedDay(value: string, preset: DatePreset): string {
  const iso = value.length === 10 ? `${value}T00:00:00.000Z` : value;
  return formatDate(iso, preset) ?? value;
}

/** "Mar 27 to Aug 15, 2026": a human range from two `YYYY-MM-DD` days, no raw ISO hyphens. */
export function formatDayRange(from: string, to: string): string {
  const start = formatDate(`${from}T00:00:00Z`, 'day-month');
  const end = formatDate(`${to}T00:00:00Z`, 'day-month-year');
  if (start == null || end == null) return `${from} to ${to}`;
  return `${start} to ${end}`;
}
